import React from "react";
import { useNavigate } from "react-router";

import { ResourceHubLink, links } from "@/models/resourceHubs";

import { Forms, areRichTextObjectsEqual } from "turboui";
import { useRichEditorHandlers } from "@/hooks/useRichEditorHandlers";
import { assertPresent } from "@/utils/assertions";
import { isValidURL } from "@/utils/validators";

import { useLoadedData } from "./loader";

import { usePaths } from "@/routes/paths";

export function Form() {
  const paths = usePaths();
  const { link } = useLoadedData();
  const navigate = useNavigate();
  const [edit] = links.useUpdate();

  assertPresent(link.name, "name must be present in link");
  assertPresent(link.url, "url must be present in link");
  assertPresent(link.resourceHubId, "resourceHubId must be present in link");

  const richTextHandlers = useRichEditorHandlers({ scope: { type: "resource_hub", id: link.resourceHubId } });

  const form = Forms.useForm({
    fields: {
      title: link.name,
      url: link.url,
      description: JSON.parse(link.description!),
    },
    validate: (addError) => {
      if (!form.values.title) {
        addError("title", "Title is required");
      }
      if (!form.values.url) {
        addError("url", "Link is required");
      }
      if (!isValidURL(form.values.url)) {
        addError("link", "Invalid link");
      }
    },
    cancel: () => {
      navigate(paths.resourceHubLinkPath(link.id!));
    },
    submit: async () => {
      const { title, url, description } = form.values;

      if (linkHasChanged(link, title, url, description)) {
        await edit({
          linkId: link.id,
          name: title,
          url: url,
          description: JSON.stringify(description),
          type: link.type,
        });
        navigate(paths.resourceHubLinkPath(link.id!));
      } else {
        navigate(paths.resourceHubLinkPath(link.id!));
      }
    },
  });

  return (
    <Forms.Form form={form}>
      <Forms.FieldGroup>
        <Forms.TextInput
          autoFocus
          label="What do you want to call this link?"
          placeholder="Type the title of this link"
          field="title"
        />

        <Forms.TextInput label="Paste the link" placeholder="eg. https://www.example.com/file/8430762" field="url" />

        <Forms.RichTextArea
          label="Notes (optional)"
          field="description"
          richTextHandlers={richTextHandlers}
          placeholder="Add any notes here..."
        />
      </Forms.FieldGroup>

      <Forms.Submit saveText="Save" buttonSize="base" />
    </Forms.Form>
  );
}

function linkHasChanged(file: ResourceHubLink, name: string, url: string, description: any) {
  if (file.name !== name) return true;
  if (file.url !== url) return true;
  if (!areRichTextObjectsEqual(JSON.parse(file.description!), description)) return true;
  return false;
}
